import React from "react";
import { Flex, Tag, Typography } from "antd";
import { HolderOutlined } from "@ant-design/icons";
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { MiniaturaMedia } from "./MiniaturaMedia";

/** Una fila de la galería. El handle es el ícono, no la fila: si no, no se puede clickear. */
const FilaArrastrable: React.FC<{
  id: string;
  children: React.ReactNode;
  extra?: React.ReactNode;
}> = ({ id, children, extra }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <Flex
      ref={setNodeRef}
      align="center"
      gap={8}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        background: isDragging ? "#f0f5ff" : "#fff",
        border: "1px solid #f0f0f0",
        borderRadius: 6,
        padding: "6px 10px",
        marginBottom: 6,
      }}
      {...attributes}
    >
      <HolderOutlined
        {...listeners}
        style={{ cursor: "grab", color: "#8c8c8c" }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
      {extra}
    </Flex>
  );
};

/**
 * Imágenes y videos del form, ordenados arrastrando (xcons-docs#479). **El orden es el
 * dato**: la primera imagen es la principal, y la posición que se guarda es la de pantalla.
 */
export function GaleriaOrdenable<T>({
  items,
  idDe,
  onMover,
  renderItem,
  renderExtra,
}: {
  items: T[];
  /** Único por item: por archivo y no por posición, así cada fila sigue a su imagen. */
  idDe: (item: T) => string;
  onMover: (desde: number, hacia: number) => void;
  renderItem: (item: T, index: number) => React.ReactNode;
  renderExtra?: (item: T, index: number) => React.ReactNode;
}) {
  const sensors = useSensors(useSensor(PointerSensor));
  const ids = items.map(idDe);

  const alSoltar = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    onMover(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={alSoltar}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {items.map((item, index) => (
          <FilaArrastrable
            key={ids[index]}
            id={ids[index]}
            extra={renderExtra?.(item, index)}
          >
            {renderItem(item, index)}
          </FilaArrastrable>
        ))}
      </SortableContext>
    </DndContext>
  );
}

/**
 * Una imagen que se saca de un producto que ya está a la venta (xcons-docs#499): **con el
 * formato de las filas que quedan**, debajo de ellas, con el fondo y el marco amarillos de lo
 * que cambia y translúcida. No se arrastra: ya no es parte del orden.
 */
export const FilaQuitada: React.FC<{ file: string; url?: string }> = ({ file, url }) => (
  <Flex
    align="center"
    gap={8}
    style={{
      background: "#fffbe6",
      border: "1px solid #fadb14",
      borderRadius: 6,
      padding: "6px 10px",
      marginBottom: 6,
    }}
  >
    {/* El lugar del handle, para que la miniatura quede alineada con las de arriba. */}
    <HolderOutlined style={{ visibility: "hidden" }} />
    <Flex align="center" gap={8} style={{ flex: 1, minWidth: 0, opacity: 0.4 }}>
      <MiniaturaMedia file={file} url={url} />
      <Typography.Text code>{file}</Typography.Text>
    </Flex>
    <Tag color="gold" style={{ marginInlineEnd: 0 }}>
      Se quita
    </Tag>
  </Flex>
);
